import type { ItemPedido, Preco } from '../db/tipos'

// A regra de quantidade de um PRECO: "este valor so vale de 24 unidades para
// cima". Mora na linha de `precos` (tabela x produto) e nao na tabela, porque
// o minimo muda de produto para produto dentro da mesma tabela -- na planilha
// da FORTE PAN o codigo 14007 chega a ir 20 -> 400 -> 200 nas tres condicoes.
//
// OS DOIS LADOS SAO OPCIONAIS, E VAZIO E O NORMAL. A esmagadora maioria dos
// precos do banco nao tem regra nenhuma, e para eles NADA nesta feature pode
// mudar de comportamento.
export interface RegraQuantidade {
  minima: number | null
  maxima: number | null
}

export type Avaliacao =
  | { fora: false }
  | { fora: true; tipo: 'abaixo' | 'acima'; limite: number }

export interface ItemForaDaRegra {
  itemId: string
  produtoId: string
  quantidade: number
  tipo: 'abaixo' | 'acima'
  limite: number
}

// ⚠ OS CAMPOS SAO OPCIONAIS AQUI DE PROPOSITO, e o `?? null` nao e decoracao.
// O registro guardado no APARELHO pode nao ter as propriedades: a migration 0018
// criou as colunas em 17/08/2026, mas o sync e POR CURSOR
// (`.gt('atualizado_em', cursor)`) -- linha que nao mudou nunca mais e puxada.
// Entao todo aparelho com preco em cache de antes disso le `undefined`, e
// `undefined !== null` e VERDADEIRO: a guarda de `textoDaRegra` deixava passar e
// o `.toLocaleString` derrubava a tela do pedido inteira (18/08/2026).
//
// ▶ Este e o funil UNICO por onde a regra sai de um preco -- por isso o conserto
// mora aqui, e nao em cada leitor. Vale para qualquer coluna nova deste projeto:
// campo recem-criado chega como `undefined` no aparelho que ja rodava.
export function regraDoPreco(
  preco: { quantidade_minima?: number | null; quantidade_maxima?: number | null },
): RegraQuantidade {
  return {
    minima: preco.quantidade_minima ?? null,
    maxima: preco.quantidade_maxima ?? null,
  }
}

export function avaliarQuantidade(quantidade: number, regra: RegraQuantidade): Avaliacao {
  if (regra.minima !== null && quantidade < regra.minima) {
    return { fora: true, tipo: 'abaixo', limite: regra.minima }
  }
  if (regra.maxima !== null && quantidade > regra.maxima) {
    return { fora: true, tipo: 'acima', limite: regra.maxima }
  }
  return { fora: false }
}

// Milhar com ponto e sem casas sobrando: "min. 1.500", nao "min. 1500,000".
function quantidadeEmTexto(n: number): string {
  return n.toLocaleString('pt-BR', { maximumFractionDigits: 3 })
}

// A ETIQUETA curta, para caber na linha da busca do pedido ao lado do preco.
export function textoDaRegra(regra: RegraQuantidade): string | null {
  const { minima, maxima } = regra
  if (minima !== null && maxima !== null) {
    return `${quantidadeEmTexto(minima)} a ${quantidadeEmTexto(maxima)} un.`
  }
  if (minima !== null) return `mín. ${quantidadeEmTexto(minima)}`
  if (maxima !== null) return `máx. ${quantidadeEmTexto(maxima)}`
  return null
}

// A FRASE do aviso. Fala da condicao da fabrica, nao de erro de quem digitou:
// vender abaixo do minimo e uma decisao legitima que a fornecedora pode aceitar.
export function avisoDaRegra(avaliacao: Avaliacao): string | null {
  if (!avaliacao.fora) return null
  return avaliacao.tipo === 'abaixo'
    ? `Este preço é a partir de ${quantidadeEmTexto(avaliacao.limite)} un.`
    : `Este preço é até ${quantidadeEmTexto(avaliacao.limite)} un.`
}

// So o MINIMO decide a quantidade inicial. Nascer no maximo faria o
// representante vender o teto sem querer -- o oposto do que a regra protege.
export function quantidadeInicial(regra: RegraQuantidade): number {
  return regra.minima ?? 1
}

// `origem_preco` guarda 'tabela:<id>' | 'negociado' | 'manual' (pedidos/precos.ts).
// Preco negociado ou digitado a mao NAO tem regra: nao existe linha de tabela
// de onde le-la.
export function tabelaDoItem(origemPreco: string): string | null {
  const prefixo = 'tabela:'
  return origemPreco.startsWith(prefixo) ? origemPreco.slice(prefixo.length) : null
}

export function itensForaDaRegra(
  itens: readonly ItemPedido[],
  precos: readonly Preco[],
): ItemForaDaRegra[] {
  // Chave composta tabela+produto: o MESMO produto tem preco em varias tabelas
  // com minimos diferentes. Indexar so por produto devolveria o minimo da
  // tabela errada, e o app avisaria um numero que nao e o daquele item.
  const porTabelaEProduto = new Map<string, Preco>()
  for (const p of precos) {
    if (p.apagado_em !== null) continue
    porTabelaEProduto.set(`${p.tabela_preco_id}:${p.produto_id}`, p)
  }

  const fora: ItemForaDaRegra[] = []
  for (const item of itens) {
    if (item.apagado_em !== null) continue
    const tabelaId = tabelaDoItem(item.origem_preco)
    if (tabelaId === null) continue
    const preco = porTabelaEProduto.get(`${tabelaId}:${item.produto_id}`)
    if (!preco) continue
    const avaliacao = avaliarQuantidade(item.quantidade, regraDoPreco(preco))
    if (!avaliacao.fora) continue
    fora.push({
      itemId: item.id,
      produtoId: item.produto_id,
      quantidade: item.quantidade,
      tipo: avaliacao.tipo,
      limite: avaliacao.limite,
    })
  }
  return fora
}
